// Vollea — Club admin "My Club" page (create + manage organisers)
import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IoChevronBack, IoBusinessOutline, IoCopyOutline, IoCheckmark, IoCheckmarkCircle, IoTimeOutline, IoTrashOutline, IoAdd, IoTrophy, IoChevronForward } from "react-icons/io5";
import { Btn, Card, Input, Avatar, Empty } from "../components/ui-vollea";
import CreateLeagueModal from "../components/CreateLeagueModal";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { C, SP, R } from "../lib/theme";

export default function MyClub() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [clubs, setClubs] = useState([]);
  const [myClub, setMyClub] = useState(null);
  const [organisers, setOrganisers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  const [leagues, setLeagues] = useState([]);
  const [showCreate, setShowCreate] = useState(false);

  // Create-form state
  const [form, setForm] = useState({ name: "", location: "", description: "" });
  const [creating, setCreating] = useState(false);
  const [createMsg, setCreateMsg] = useState("");
  const [createOk, setCreateOk] = useState(false);

  const loadClubs = useCallback(async () => {
    setLoading(true);
    try {
      const all = await api.get("/clubs/mine");
      setClubs(all || []);
      const mine = (all || []).find((c) => c.admin_id === user?.user_id);
      setMyClub(mine || null);
      if (mine) {
        try { setOrganisers(await api.get(`/clubs/${mine.club_id}/organisers`) || []); }
        catch { setOrganisers([]); }
        try { setLeagues(await api.get(`/leagues?managed=true`) || []); }
        catch { setLeagues([]); }
      } else {
        setOrganisers([]);
        setLeagues([]);
      }
    } catch {}
    finally { setLoading(false); }
  }, [user?.user_id]);

  useEffect(() => { loadClubs(); }, [loadClubs]);

  const createClub = async (e) => {
    e?.preventDefault?.();
    setCreating(true); setCreateMsg(""); setCreateOk(false);
    try {
      const c = await api.post("/clubs", form);
      setCreateOk(true);
      setCreateMsg(c.approved ? "Club created and approved!" : "Club created — pending super-admin approval.");
      await loadClubs();
    } catch (e) { setCreateOk(false); setCreateMsg(e.message); }
    finally { setCreating(false); }
  };

  const approveOrganiser = async (coId) => {
    await api.post(`/club-organisers/${coId}/approve`);
    loadClubs();
  };

  const removeOrganiser = async (coId) => {
    await api.del(`/club-organisers/${coId}`);
    loadClubs();
  };

  const copyCode = () => {
    if (!myClub?.code) return;
    navigator.clipboard?.writeText(myClub.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const label = { color: C.textMuted, fontSize: 11, fontWeight: 600, letterSpacing: 1, marginBottom: 6, marginTop: SP.md, textTransform: "uppercase", display: "block" };
  const input = { width: "100%", background: C.surface2, borderRadius: R.md, border: `1px solid ${C.border}`, color: C.text, padding: `0 ${SP.lg}px`, height: 52, fontSize: 15, outline: "none" };

  if (loading) {
    return <div style={{ padding: SP.xl, textAlign: "center", color: C.lime }}>Loading…</div>;
  }

  return (
    <div style={{ background: C.bg, minHeight: "100vh", padding: `${SP.lg}px ${SP.lg}px 90px` }}>
      <button onClick={() => navigate(-1)} data-testid="myclub-back" style={{ color: C.text, marginBottom: SP.md }}>
        <IoChevronBack size={26} />
      </button>
      <h1 style={{ color: C.text, fontWeight: 700, fontSize: 28, margin: 0, letterSpacing: 0.5 }}>My Club</h1>
      <p style={{ color: C.textMuted, fontSize: 14, marginTop: 4 }}>
        {myClub ? "Manage your club and approve organisers." : "Register your club to start hosting leagues on Vollea."}
      </p>

      {createMsg ? (
        <div
          data-testid="myclub-create-msg"
          style={{
            marginTop: SP.lg,
            background: createOk ? C.limeTint : "rgba(239,68,68,0.1)",
            border: `1px solid ${createOk ? C.lime : C.error}`,
            color: createOk ? C.lime : C.error,
            padding: SP.md, borderRadius: R.md, fontSize: 13, fontWeight: 600,
          }}
        >
          {createMsg}
        </div>
      ) : null}

      {!myClub ? (
        <Card style={{ marginTop: SP.xl }}>
          <form onSubmit={createClub}>
            <label style={label}>Club name</label>
            <input data-testid="myclub-name-input" style={input} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required />

            <label style={label}>Location</label>
            <input data-testid="myclub-location-input" style={input} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="City, country" required />

            <label style={label}>Description (optional)</label>
            <textarea data-testid="myclub-description-input" rows={3} style={{ ...input, height: 90, padding: SP.lg, resize: "vertical" }} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Tell players a bit about your courts…" />

            <div style={{ height: SP.lg }} />
            <Btn label="Create club" loading={creating} testid="myclub-create-button" type="submit" onClick={createClub} icon={<IoBusinessOutline size={16} />} />
          </form>
        </Card>
      ) : (
        <>
          <Card style={{ marginTop: SP.xl }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: SP.md }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>CLUB</div>
                <div data-testid="myclub-name" style={{ color: C.text, fontWeight: 700, fontSize: 20, marginTop: 4 }}>{myClub.name}</div>
                <div style={{ color: C.textMuted, fontSize: 13, marginTop: 2 }}>{myClub.location}</div>
              </div>
              <StatusChip approved={myClub.approved} testid={`myclub-status-${myClub.approved ? "approved" : "pending"}`} />
            </div>
            <div style={{ display: "flex", gap: SP.md, marginTop: SP.lg, paddingTop: SP.md, borderTop: `1px solid ${C.divider}` }}>
              <div style={{ flex: 1 }}>
                <div style={{ color: C.textMuted, fontSize: 11, fontWeight: 700, letterSpacing: 1 }}>CLUB CODE</div>
                <button onClick={copyCode} data-testid="myclub-copy-code" style={{ color: copied ? C.lime : C.text, fontWeight: 700, fontSize: 18, fontFamily: "monospace", letterSpacing: 4, marginTop: 4, display: "inline-flex", alignItems: "center", gap: 6 }}>
                  {myClub.code} {copied ? <IoCheckmark size={14} color={C.lime} /> : <IoCopyOutline size={14} color={C.textDim} />}
                </button>
                <div style={{ color: C.textMuted, fontSize: 12, marginTop: 6 }}>Share this with organisers to join.</div>
              </div>
            </div>
            {!myClub.approved && (
              <div data-testid="myclub-pending-banner" style={{ marginTop: SP.md, background: "rgba(245,158,11,0.1)", border: `1px solid ${C.warning}`, padding: SP.md, borderRadius: R.md, color: C.warning, fontSize: 13, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6 }}>
                <IoTimeOutline size={16} /> Waiting for super-admin approval before you can run leagues.
              </div>
            )}
          </Card>

          <div style={{ marginTop: SP.xl }}>
            <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 700, letterSpacing: 1.2, marginBottom: SP.md, textTransform: "uppercase" }}>
              Organisers ({organisers.length})
            </div>
            <div data-testid="organisers-list" style={{ display: "flex", flexDirection: "column", gap: SP.sm }}>
              {organisers.length === 0 ? (
                <Empty text="No organisers yet. Share your club code with people you trust." />
              ) : organisers.map((o) => (
                <Card key={o.co_id} testid={`organiser-row-${o.co_id}`}>
                  <div style={{ display: "flex", alignItems: "center", gap: SP.md }}>
                    <Avatar uri={o.photo} name={o.name} size={40} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: C.text, fontWeight: 700, fontSize: 14 }}>{o.name}</div>
                      <div style={{ color: C.textMuted, fontSize: 12 }}>{o.email}</div>
                    </div>
                    <OrganiserStatus status={o.status} />
                    <div style={{ display: "flex", gap: 6 }}>
                      {o.status === "pending" && (
                        <button onClick={() => approveOrganiser(o.co_id)} data-testid={`organiser-approve-${o.co_id}`} style={{ padding: "6px 10px", borderRadius: R.sm, background: C.limeTint, color: C.lime, fontSize: 11, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4, letterSpacing: 1, textTransform: "uppercase" }}>
                          <IoCheckmark size={12} /> Approve
                        </button>
                      )}
                      <button onClick={() => removeOrganiser(o.co_id)} data-testid={`organiser-remove-${o.co_id}`} style={{ padding: "6px 8px", borderRadius: R.sm, background: C.surface3, color: C.error }}>
                        <IoTrashOutline size={14} />
                      </button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>

          <div style={{ marginTop: SP.xl }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: SP.md }}>
              <div style={{ color: C.textMuted, fontSize: 12, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase" }}>
                Your Leagues ({leagues.length})
              </div>
              <button
                onClick={() => setShowCreate(true)}
                disabled={!myClub.approved}
                data-testid="myclub-new-league"
                title={myClub.approved ? "" : "Club must be approved first"}
                style={{
                  display: "inline-flex", alignItems: "center", gap: 4,
                  padding: "6px 12px", borderRadius: R.pill,
                  background: C.lime, color: C.onLime,
                  fontWeight: 700, fontSize: 11, letterSpacing: 1, textTransform: "uppercase",
                  opacity: myClub.approved ? 1 : 0.4,
                }}
              >
                <IoAdd size={14} /> New
              </button>
            </div>
            <div data-testid="myclub-leagues-list" style={{ display: "flex", flexDirection: "column", gap: SP.sm }}>
              {leagues.length === 0 ? (
                <Empty text="No leagues yet. Tap + New to launch your first one." />
              ) : leagues.map((l) => (
                <Card key={l.league_id} onClick={() => navigate(`/leagues/${l.league_id}`)} testid={`myclub-league-${l.league_id}`}>
                  <div style={{ display: "flex", alignItems: "center", gap: SP.md }}>
                    <div style={{ width: 40, height: 40, borderRadius: R.md, background: C.limeTint, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <IoTrophy size={20} color={C.lime} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ color: C.text, fontWeight: 700, fontSize: 15 }}>{l.name}</div>
                      <div style={{ color: C.textMuted, fontSize: 12 }}>
                        {l.member_count ?? 0} players · {(l.divisions || []).length} div · Code {l.code}
                      </div>
                    </div>
                    <IoChevronForward size={20} color={C.textDim} />
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </>
      )}

      {user?.role === "super_admin" && clubs.length > 0 && (
        <div style={{ marginTop: SP.xl }}>
          <p style={{ color: C.textMuted, fontSize: 12 }}>
            Tip: as super-admin you can also view the global club approval queue in
            {" "}
            <button onClick={() => navigate("/admin/clubs")} style={{ color: C.lime, fontWeight: 600 }}>Admin → Clubs</button>.
          </p>
        </div>
      )}

      {showCreate && myClub && (
        <CreateLeagueModal
          mode="club"
          clubId={myClub.club_id}
          onClose={() => setShowCreate(false)}
          onCreated={loadClubs}
        />
      )}
    </div>
  );
}

function StatusChip({ approved, testid }) {
  const conf = approved
    ? { bg: C.limeTint, fg: C.lime, text: "Approved", Icon: IoCheckmarkCircle }
    : { bg: "rgba(245,158,11,0.15)", fg: C.warning, text: "Pending", Icon: IoTimeOutline };
  const { Icon } = conf;
  return (
    <span data-testid={testid} style={{ display: "inline-flex", alignItems: "center", gap: 4, background: conf.bg, color: conf.fg, fontSize: 10, fontWeight: 700, padding: "4px 10px", borderRadius: R.pill, letterSpacing: 1, textTransform: "uppercase" }}>
      <Icon size={12} /> {conf.text}
    </span>
  );
}

function OrganiserStatus({ status }) {
  const conf = {
    approved: { bg: C.limeTint, fg: C.lime, text: "Approved" },
    pending: { bg: "rgba(245,158,11,0.15)", fg: C.warning, text: "Pending" },
  }[status] || { bg: C.surface3, fg: C.textMuted, text: status };
  return (
    <span style={{ background: conf.bg, color: conf.fg, fontSize: 9, fontWeight: 700, padding: "2px 8px", borderRadius: R.pill, letterSpacing: 1, textTransform: "uppercase" }}>
      {conf.text}
    </span>
  );
}
